import { Injectable, Optional } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { fromEvent, interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })


export class UpdateService {
  private readonly CHECK_INTERVAL = 60 * 60 * 1000; // 1h — drop to 60_000 for snappier

  private subs = new Subscription();
  private reloading = false;

  // call guard: never reload mid-call
  private callActive = false;
  private pendingReload = false;

  constructor(
    @Optional() private _swUpdate?: SwUpdate
  ) { }

  initializeUpdateService(): void {
    if (!this._swUpdate?.isEnabled) {
      console.warn('SwUpdate not enabled');
      return;
    }

    // new version fully downloaded & cached → safe to swap
    this.subs.add(
      this._swUpdate.versionUpdates
        .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
        .subscribe(() => this.applyUpdate())
    );

    // SW wedged in an unrecoverable state
    this.subs.add(this._swUpdate.unrecoverable.subscribe(() => this.hardReload()));

    // check now, then periodically, then whenever the tab regains focus
    this.check();
    this.subs.add(interval(this.CHECK_INTERVAL).subscribe(() => this.check()));
    this.subs.add(
      fromEvent(document, 'visibilitychange').subscribe(() => {
        if (document.visibilityState === 'visible') this.check();
      })
    );
  }

  /** call from EnhancedPeerJSService when a call starts / ends */
  setCallActive(active: boolean): void {
    this.callActive = active;
    if (!active && this.pendingReload) this.applyUpdate();
  }

  private async check(): Promise<void> {
    if (!this._swUpdate?.isEnabled) return;
    try { await this._swUpdate.checkForUpdate(); }
    catch (err) { console.warn('SW update check failed', err); }
  }

  private async applyUpdate(): Promise<void> {
    if (this.callActive) { this.pendingReload = true; return; }
    try { await this._swUpdate?.activateUpdate(); }
    catch (err) { console.warn('activateUpdate failed', err); }
    this.hardReload();
  }

  private hardReload(): void {
    if (this.reloading) return;
    this.reloading = true;
    document.location.reload();
  }

  destroy(): void {
    this.subs.unsubscribe();
  }
}